import React, { useState, useCallback, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { FileCode, Files, Plus, X, Info, Maximize2, Minimize2, Copy, Check, Save, Wand2 } from 'lucide-react';
import { useLLDPractice, FileNode, Language, UMLElement } from '../../context/LLDPracticeContext';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface LanguageConfig {
  label: string;
  monacoLanguage: string;
  extension: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const LANGUAGE_CONFIG: Record<Language, LanguageConfig> = {
  typescript: { label: 'TypeScript', monacoLanguage: 'typescript', extension: '.ts' },
  javascript: { label: 'JavaScript', monacoLanguage: 'javascript', extension: '.js' },
  python: { label: 'Python', monacoLanguage: 'python', extension: '.py' },
};

const BOILERPLATE_TEMPLATES: Record<Language, FileNode[]> = {
  typescript: [
    {
      path: 'ParkingLot.ts',
      content: `class ParkingLot {\n  // TODO: implement\n}\n`,
    },
  ],
  javascript: [
    {
      path: 'ParkingLot.js',
      content: `class ParkingLot {\n  // TODO: implement\n}\n\nexport default ParkingLot;\n`,
    },
  ],
  python: [
    {
      path: 'parking_lot.py',
      content: `class ParkingLot:\n    \"\"\"Coordinates parking, ticketing, and availability.\"\"\"\n\n    def __init__(self):\n        # TODO: implement\n        pass\n`,
    },
  ],
};

const MAX_FILES = 20;
const DEBOUNCE_DELAY_MS = 500;

type FileStructure = 'single' | 'separate';

const accessWord = (access: '+' | '-' | '#') => access === '+' ? 'public' : access === '#' ? 'protected' : 'private';

const methodParts = (signature: string) => {
  const match = signature.match(/^\s*([\w$]+)\s*\((.*?)\)\s*(?::\s*(.+))?$/);
  return match
    ? { name: match[1], parameters: match[2], returnType: (match[3] || 'void').trim() }
    : { name: signature.trim() || 'method', parameters: '', returnType: 'void' };
};

const referencedElements = (element: UMLElement, elements: UMLElement[]) => {
  const source = [
    ...element.fields.map((field) => field.type),
    ...element.methods.map((method) => method.signature),
  ].join(' ');
  return elements.filter((candidate) =>
    candidate.id !== element.id && new RegExp(`\\b${candidate.name}\\b`).test(source)
  );
};

const typescriptElement = (element: UMLElement, exported: boolean) => {
  const prefix = exported ? 'export ' : '';
  if (element.type === 'interface') {
    const fields = element.fields.map((field) => `  ${field.name}: ${field.type};`);
    const methods = element.methods.map((method) => `  ${method.signature};`);
    return `${prefix}interface ${element.name} {\n${[...fields, ...methods].join('\n')}\n}`;
  }

  const abstract = element.type === 'abstract-class';
  const fields = element.fields.map((field) => `  ${accessWord(field.access)} ${field.name}: ${field.type};`);
  const methods = element.methods.map((method) => {
    if (abstract) return `  abstract ${method.signature};`;
    return `  ${accessWord(method.access)} ${method.signature} {\n    throw new Error('Not implemented');\n  }`;
  });
  return `${prefix}${abstract ? 'abstract ' : ''}class ${element.name} {\n${[...fields, ...methods].join('\n\n')}\n}`;
};

const javascriptElement = (element: UMLElement, exported: boolean) => {
  const fields = element.fields.map((field) => `  ${field.name};`);
  const methods = element.methods.map((method) => {
    const parsed = methodParts(method.signature);
    const parameters = parsed.parameters.split(',').map((parameter) => parameter.trim().split(':')[0]).filter(Boolean).join(', ');
    return `  ${parsed.name}(${parameters}) {\n    throw new Error('Not implemented');\n  }`;
  });
  return `${exported ? 'export ' : ''}class ${element.name} {\n${[...fields, ...methods].join('\n\n')}\n}`;
};

const pythonElement = (element: UMLElement) => {
  const assignments = element.fields.length
    ? element.fields.map((field) => `        self.${field.name}: ${field.type} = None`).join('\n')
    : '        pass';
  const methods = element.methods.map((method) => {
    const parsed = methodParts(method.signature);
    const parameters = parsed.parameters
      ? `, ${parsed.parameters.split(',').map((parameter) => parameter.trim().split(':')[0]).filter(Boolean).join(', ')}`
      : '';
    return `    def ${parsed.name}(self${parameters}) -> ${parsed.returnType}:\n        raise NotImplementedError`;
  });
  return `class ${element.name}:\n    def __init__(self):\n${assignments}${methods.length ? `\n\n${methods.join('\n\n')}` : ''}\n`;
};

function generateFromUML(language: Language, structure: FileStructure, elements: UMLElement[]): FileNode[] {
  const safeElements = elements.slice(0, structure === 'separate' ? MAX_FILES : elements.length);
  const extension = LANGUAGE_CONFIG[language].extension;

  if (structure === 'single') {
    const content = safeElements.map((element) =>
      language === 'typescript'
        ? typescriptElement(element, false)
        : language === 'javascript'
          ? javascriptElement(element, false)
          : pythonElement(element)
    ).join('\n\n');
    return [{ path: `Design${extension}`, content: `${content}\n` }];
  }

  return safeElements.map((element) => {
    const references = referencedElements(element, safeElements);
    const imports = language === 'typescript'
      ? references.map((reference) => `import { ${reference.name} } from './${reference.name}';`).join('\n')
      : language === 'javascript'
        ? references.map((reference) => `import { ${reference.name} } from './${reference.name}.js';`).join('\n')
        : references.map((reference) => `from .${reference.name} import ${reference.name}`).join('\n');
    const declaration = language === 'typescript'
      ? typescriptElement(element, true)
      : language === 'javascript'
        ? javascriptElement(element, true)
        : pythonElement(element);
    return {
      path: `${element.name}${extension}`,
      content: `${imports ? `${imports}\n\n` : ''}${declaration}\n`,
    };
  });
}

function newFileStarter(language: Language, fileName: string): string {
  const baseName = fileName.split('/').pop()?.replace(/\.[^.]+$/, '') || 'NewClass';
  const className = baseName.replace(/[^a-zA-Z0-9_$]/g, '') || 'NewClass';
  if (language === 'typescript') return `export class ${className} {\n  // TODO: implement\n}\n`;
  if (language === 'javascript') return `export class ${className} {\n  // TODO: implement\n}\n`;
  return `class ${className}:\n    def __init__(self):\n        pass\n`;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const CodeEditorPanel: React.FC = () => {
  const { state, dispatch } = useLLDPractice();
  const { codeFiles, activeLanguage, umlDiagram } = state.workspaceState;

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(activeLanguage);
  const [activeFileIndex, setActiveFileIndex] = useState(0);
  const [showNewFileInput, setShowNewFileInput] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFileError, setNewFileError] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [fileStructure, setFileStructure] = useState<FileStructure>(
    codeFiles[activeLanguage]?.length > 1 ? 'separate' : 'single'
  );
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const managedModelUrisRef = useRef<Set<string>>(new Set());
  const pythonCompletionRef = useRef<any>(null);

  // Get current files for the selected language
  const currentFiles: FileNode[] =
    codeFiles[selectedLanguage]?.length > 0
      ? codeFiles[selectedLanguage]
      : BOILERPLATE_TEMPLATES[selectedLanguage];
  const activeFile = currentFiles[activeFileIndex] || currentFiles[0];
  const workspaceId = (state.problemState.problemId || 'workspace').replace(/[^a-zA-Z0-9_-]/g, '-');

  const modelUriFor = useCallback((monaco: any, language: Language, path: string) =>
    monaco.Uri.parse(`file:///lld/${workspaceId}/${language}/${path}`), [workspaceId]);

  const syncWorkspaceModels = useCallback((monaco: any, language: Language, files: FileNode[]) => {
    const nextUris = new Set<string>();
    files.forEach((file) => {
      const uri = modelUriFor(monaco, language, file.path);
      const uriString = uri.toString();
      nextUris.add(uriString);
      const existing = monaco.editor.getModel(uri);
      if (!existing) {
        monaco.editor.createModel(file.content, LANGUAGE_CONFIG[language].monacoLanguage, uri);
      } else if (existing !== editorRef.current?.getModel() && existing.getValue() !== file.content) {
        existing.setValue(file.content);
      }
    });

    managedModelUrisRef.current.forEach((uriString) => {
      if (!nextUris.has(uriString)) {
        monaco.editor.getModel(monaco.Uri.parse(uriString))?.dispose();
      }
    });
    managedModelUrisRef.current = nextUris;
  }, [modelUriFor]);

  // Ensure active file index is within bounds
  useEffect(() => {
    if (activeFileIndex >= currentFiles.length) {
      setActiveFileIndex(Math.max(0, currentFiles.length - 1));
    }
  }, [currentFiles.length, activeFileIndex]);

  // Initialize code files with boilerplate if empty
  useEffect(() => {
    if (!codeFiles[selectedLanguage] || codeFiles[selectedLanguage].length === 0) {
      dispatch({
        type: 'UPDATE_CODE',
        payload: {
          language: selectedLanguage,
          files: BOILERPLATE_TEMPLATES[selectedLanguage],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  // Keep every file registered with Monaco, not only files that have been
  // opened. This lets TypeScript/JavaScript resolve relative imports and offer
  // cross-file symbols immediately.
  useEffect(() => {
    if (monacoRef.current) {
      syncWorkspaceModels(monacoRef.current, selectedLanguage, currentFiles);
    }
  }, [currentFiles, selectedLanguage, syncWorkspaceModels]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleLanguageChange = useCallback(
    (newLang: Language) => {
      setSelectedLanguage(newLang);
      setActiveFileIndex(0);
      setFileStructure(codeFiles[newLang]?.length > 1 ? 'separate' : 'single');

      // Load boilerplate if no files exist for this language
      if (!codeFiles[newLang] || codeFiles[newLang].length === 0) {
        dispatch({
          type: 'UPDATE_CODE',
          payload: {
            language: newLang,
            files: BOILERPLATE_TEMPLATES[newLang],
          },
        });
      } else {
        // Just switch active language
        dispatch({
          type: 'UPDATE_CODE',
          payload: {
            language: newLang,
            files: codeFiles[newLang],
          },
        });
      }
    },
    [codeFiles, dispatch]
  );

  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      if (value === undefined) return;

      // Debounced dispatch
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        const updatedFiles = [...currentFiles];
        updatedFiles[activeFileIndex] = {
          ...updatedFiles[activeFileIndex],
          content: value,
        };

        dispatch({
          type: 'UPDATE_CODE',
          payload: {
            language: selectedLanguage,
            files: updatedFiles,
          },
        });
      }, DEBOUNCE_DELAY_MS);
    },
    [currentFiles, activeFileIndex, selectedLanguage, dispatch]
  );

  const handleAddFile = useCallback(() => {
    const trimmedName = newFileName.trim();
    if (!trimmedName) {
      setNewFileError('File name is required');
      return;
    }

    if (currentFiles.length >= MAX_FILES) {
      setNewFileError(`Maximum of ${MAX_FILES} files reached`);
      return;
    }

    // Check nesting depth
    const depth = trimmedName.split('/').length;
    if (depth > 5) {
      setNewFileError('Maximum directory nesting depth is 5');
      return;
    }

    // Check duplicate
    if (currentFiles.some((f) => f.path === trimmedName)) {
      setNewFileError('File already exists');
      return;
    }

    // Add appropriate extension if missing
    let fileName = trimmedName;
    const ext = LANGUAGE_CONFIG[selectedLanguage].extension;
    if (!fileName.endsWith(ext)) {
      fileName += ext;
    }

    const updatedFiles = [
      ...currentFiles,
      { path: fileName, content: newFileStarter(selectedLanguage, fileName) },
    ];

    dispatch({
      type: 'UPDATE_CODE',
      payload: {
        language: selectedLanguage,
        files: updatedFiles,
      },
    });

    setActiveFileIndex(updatedFiles.length - 1);
    setFileStructure('separate');
    setNewFileName('');
    setNewFileError(null);
    setShowNewFileInput(false);
  }, [newFileName, currentFiles, selectedLanguage, dispatch]);

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (currentFiles.length <= 1) return; // Keep at least one file

      const updatedFiles = currentFiles.filter((_, i) => i !== index);
      dispatch({
        type: 'UPDATE_CODE',
        payload: {
          language: selectedLanguage,
          files: updatedFiles,
        },
      });

      if (activeFileIndex >= updatedFiles.length) {
        setActiveFileIndex(updatedFiles.length - 1);
      } else if (activeFileIndex === index) {
        setActiveFileIndex(0);
      }
      if (updatedFiles.length === 1) setFileStructure('single');
    },
    [currentFiles, selectedLanguage, activeFileIndex, dispatch]
  );

  const handleNewFileKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddFile();
    } else if (e.key === 'Escape') {
      setShowNewFileInput(false);
      setNewFileName('');
      setNewFileError(null);
    }
  };

  const saveCurrentFile = useCallback(() => {
    const value = editorRef.current?.getValue();
    if (typeof value !== 'string') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const updatedFiles = [...currentFiles];
    updatedFiles[activeFileIndex] = { ...updatedFiles[activeFileIndex], content: value };
    dispatch({ type: 'UPDATE_CODE', payload: { language: selectedLanguage, files: updatedFiles } });
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1400);
  }, [activeFileIndex, currentFiles, dispatch, selectedLanguage]);

  const applyUMLGeneration = useCallback(() => {
    if (umlDiagram.elements.length === 0) return;
    const files = generateFromUML(selectedLanguage, fileStructure, umlDiagram.elements);
    dispatch({ type: 'UPDATE_CODE', payload: { language: selectedLanguage, files } });
    setActiveFileIndex(0);
    setShowGenerateConfirm(false);
  }, [dispatch, fileStructure, selectedLanguage, umlDiagram.elements]);

  const handleGenerateFromUML = useCallback(() => {
    const hasMeaningfulCode = currentFiles.some((file) =>
      file.content.trim() && !file.content.includes('// TODO: implement')
    );
    if (hasMeaningfulCode) {
      setShowGenerateConfirm(true);
      return;
    }
    applyUMLGeneration();
  }, [applyUMLGeneration, currentFiles]);

  const copyCurrentFile = useCallback(async () => {
    const value = editorRef.current?.getValue() ?? activeFile?.content ?? '';
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }, [activeFile?.content]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMaximized) setIsMaximized(false);
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        saveCurrentFile();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMaximized, saveCurrentFile]);

  useEffect(() => () => {
    pythonCompletionRef.current?.dispose();
    const monaco = monacoRef.current;
    if (monaco) {
      managedModelUrisRef.current.forEach((uriString) =>
        monaco.editor.getModel(monaco.Uri.parse(uriString))?.dispose()
      );
    }
  }, []);

  const handleEditorMount = useCallback((editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      allowNonTsExtensions: true,
      allowJs: true,
      noEmit: true,
      strict: false,
    });
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      allowNonTsExtensions: true,
      allowJs: true,
      noEmit: true,
    });

    syncWorkspaceModels(monaco, selectedLanguage, currentFiles);

    if (!pythonCompletionRef.current) {
      const pythonWords = [
        ['class', 'class ${1:Name}:\n    def __init__(self):\n        ${0:pass}'],
        ['def', 'def ${1:method}(self${2:, args}):\n    ${0:pass}'],
        ['property', '@property\ndef ${1:name}(self):\n    return self._${1:name}'],
        ['dataclass', '@dataclass\nclass ${1:Name}:\n    ${2:field}: ${3:str}'],
        ['__init__', 'def __init__(self${1:, args}):\n    ${0:pass}'],
        ['self', 'self'], ['super', 'super()'], ['isinstance', 'isinstance(${1:object}, ${2:type})'],
        ['enumerate', 'enumerate(${1:items})'], ['len', 'len(${1:value})'], ['range', 'range(${1:count})'],
        ['dict', 'dict'], ['list', 'list'], ['set', 'set'], ['tuple', 'tuple'],
        ['raise', 'raise ${1:Exception}(${2:message})'], ['try', 'try:\n    ${1:pass}\nexcept ${2:Exception} as error:\n    ${0:raise}'],
      ];
      pythonCompletionRef.current = monaco.languages.registerCompletionItemProvider('python', {
        triggerCharacters: ['.', '@'],
        provideCompletionItems: (model: any, position: any) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
          };
          return {
            suggestions: pythonWords.map(([label, insertText]) => ({
              label,
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText,
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              range,
            })),
          };
        },
      });
    }
  }, [currentFiles, selectedLanguage, syncWorkspaceModels]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={`lld-code-editor flex flex-col h-full overflow-hidden ${isMaximized ? 'is-maximized' : ''}`}>
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex items-center gap-3">
          {/* Language Selector */}
          <select
            value={selectedLanguage}
            onChange={(e) => handleLanguageChange(e.target.value as Language)}
            className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400 cursor-pointer"
          >
            {Object.entries(LANGUAGE_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>
                {config.label}
              </option>
            ))}
          </select>

        </div>

        <div className="lld-code-editor__actions flex items-center gap-1.5">
          <span className="hidden lg:inline-flex items-center gap-1.5 px-2 py-1 text-[11px] text-gray-500 dark:text-gray-400">
            <Info className="w-3.5 h-3.5" /> Autosaved
          </span>
          <button onClick={saveCurrentFile} className="lld-editor-tool" title="Save current file (Ctrl/Cmd+S)" aria-label="Save current file">
            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
          </button>
          <button onClick={copyCurrentFile} className="lld-editor-tool" title="Copy current file" aria-label="Copy current file">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setIsMaximized((value) => !value)}
            className="lld-editor-tool"
            title={isMaximized ? 'Restore editor (Esc)' : 'Maximize editor'}
            aria-label={isMaximized ? 'Restore editor' : 'Maximize editor'}
          >
            {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* New File Input */}
      {showNewFileInput && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
          <input
            type="text"
            value={newFileName}
            onChange={(e) => {
              setNewFileName(e.target.value);
              if (newFileError) setNewFileError(null);
            }}
            onKeyDown={handleNewFileKeyDown}
            placeholder={`filename${LANGUAGE_CONFIG[selectedLanguage].extension}`}
            className="flex-1 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-400"
            autoFocus
          />
          <button
            onClick={handleAddFile}
            className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Add
          </button>
          <button
            onClick={() => {
              setShowNewFileInput(false);
              setNewFileName('');
              setNewFileError(null);
            }}
            className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
          {newFileError && (
            <span className="text-xs text-rose-500 dark:text-rose-400">{newFileError}</span>
          )}
        </div>
      )}

      <div className="lld-implementation-setup">
        <div className="lld-implementation-setup__copy">
          <strong>Code structure</strong>
          <span>{fileStructure === 'single' ? 'Fastest for an interview: keep related classes together.' : 'Production style: one exported class per file with imports.'}</span>
        </div>
        <div className="lld-structure-switch" role="group" aria-label="Code file structure">
          <button type="button" className={fileStructure === 'single' ? 'is-active' : ''} onClick={() => setFileStructure('single')}>
            <FileCode aria-hidden="true" /> One file
          </button>
          <button type="button" className={fileStructure === 'separate' ? 'is-active' : ''} onClick={() => setFileStructure('separate')}>
            <Files aria-hidden="true" /> Separate files
          </button>
        </div>
        <button type="button" className="lld-generate-code" onClick={handleGenerateFromUML} disabled={umlDiagram.elements.length === 0}>
          <Wand2 aria-hidden="true" /> Generate from UML
        </button>
      </div>

      {showGenerateConfirm && (
        <div className="lld-generate-confirm" role="alert">
          <span>Generating will replace the current {selectedLanguage} files.</span>
          <button type="button" onClick={applyUMLGeneration}>Replace and generate</button>
          <button type="button" onClick={() => setShowGenerateConfirm(false)}>Cancel</button>
        </div>
      )}

      <div className="lld-code-guidance">
        <Info aria-hidden="true" />
        <span>Class method signatures alone are design pseudocode in TypeScript. Working classes need method bodies; interfaces and abstract methods may use signatures only.</span>
      </div>

      {/* Main Content: File List + Editor */}
      <div className="flex flex-1 min-h-0">
        {/* File explorer is the single place for selecting and managing files. */}
          <div className="w-48 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 overflow-y-auto flex-shrink-0">
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-800">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-500 uppercase tracking-wide">
                Files
              </h4>
              {currentFiles.length < MAX_FILES && (
                <button
                  type="button"
                  onClick={() => setShowNewFileInput(true)}
                  className="p-1 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                  title="Add file"
                  aria-label="Add file"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="py-1">
              {currentFiles.map((file, index) => (
                <div
                  key={file.path}
                  className={`group flex items-center transition-colors ${index === activeFileIndex
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                >
                  <button
                    type="button"
                    onClick={() => setActiveFileIndex(index)}
                    className="min-w-0 flex flex-1 items-center gap-2 px-3 py-1.5 text-left text-sm"
                    title={file.path}
                  >
                    <FileCode className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{file.path}</span>
                  </button>
                  {currentFiles.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="mr-2 rounded p-1 text-gray-500 opacity-0 transition hover:bg-gray-200 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-rose-400"
                      title={`Remove ${file.path}`}
                      aria-label={`Remove ${file.path}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

        {/* Monaco Editor */}
        <div className="flex-1 min-w-0">
          <Editor
            height="100%"
            path={`file:///lld/${workspaceId}/${selectedLanguage}/${activeFile?.path || `untitled${LANGUAGE_CONFIG[selectedLanguage].extension}`}`}
            language={LANGUAGE_CONFIG[selectedLanguage].monacoLanguage}
            value={activeFile?.content || ''}
            onChange={handleEditorChange}
            onMount={handleEditorMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              wordWrap: 'on',
              suggestOnTriggerCharacters: true,
              quickSuggestions: true,
              wordBasedSuggestions: 'currentDocument',
              parameterHints: { enabled: true },
              formatOnPaste: true,
              formatOnType: selectedLanguage !== 'python',
              autoIndent: 'full',
              autoClosingBrackets: 'always',
              autoClosingQuotes: 'always',
              padding: { top: 8 },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeEditorPanel;
